import { useEffect } from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple, MapPin, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { SITE, WA_LINK } from "@/constants/site";
import LeadForm from "@/components/landing/LeadForm";

const CHANNELS = [
  {
    icon: Phone,
    label: "Call a Property Advisor",
    value: SITE.phoneDisplay,
    href: `tel:${SITE.phoneRaw}`,
    testid: "contact-call-link",
  },
  {
    icon: WhatsappLogo,
    label: "WhatsApp a Property Advisor",
    value: "Chat instantly",
    href: WA_LINK,
    testid: "contact-whatsapp-link",
  },
  {
    icon: EnvelopeSimple,
    label: "Email",
    value: SITE.email,
    href: `mailto:${SITE.email}`,
    testid: "contact-email-link",
  },
];

export default function ContactPage() {
  useEffect(() => {
    document.title = "Contact VR Ahmedabad Realtors | Property Advisors in Ahmedabad";
    const meta = document.querySelector("meta[name='description']");
    const prev = meta?.content;
    if (meta)
      meta.content =
        "Contact VR Ahmedabad Realtors — call, WhatsApp or email our Ahmedabad property advisors for luxury homes, Adani Shantigram flats, commercial property, resale and rentals.";
    return () => {
      if (meta && prev) meta.content = prev;
    };
  }, []);

  return (
    <main data-testid="contact-page" className="bg-cream font-sans text-slate-800 antialiased">
      <section className="grain bg-forest py-24">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
          <Link to="/" className="flex w-fit items-center gap-3">
            <img src="/vr-logo.jpeg" alt="VR Ahmedabad Realtors logo" width="48" height="48" className="h-12 w-12 rounded-sm object-cover ring-1 ring-gold/50" />
            <span className="font-serif text-lg text-white">VR Ahmedabad Realtors</span>
          </Link>
          <h1 className="mt-10 max-w-3xl font-serif text-4xl tracking-tighter text-white sm:text-5xl">
            Contact VR Ahmedabad Realtors
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75">
            Buying, selling, renting or investing in Ahmedabad property — start with a conversation.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:grid-cols-12 lg:gap-16 lg:px-12">
          <div className="md:col-span-5">
            <div className="space-y-4">
              {CHANNELS.map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={c.testid}
                  className="flex items-center gap-4 rounded-sm border border-forest/15 bg-white p-5 transition-colors duration-200 hover:border-gold"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-forest text-gold">
                    <c.icon size={20} weight="fill" />
                  </span>
                  <span>
                    <span className="block text-xs uppercase tracking-widest text-slate-400">{c.label}</span>
                    <span className="block font-semibold text-slate-900">{c.value}</span>
                  </span>
                </a>
              ))}
            </div>
            <div className="mt-6 rounded-sm border border-forest/15 bg-white p-5">
              <p className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
                <MapPin size={14} weight="fill" className="text-gold" /> Location
              </p>
              <p className="mt-2 font-semibold text-slate-900">Ahmedabad, Gujarat, India</p>
              <p className="mt-1 text-sm text-slate-500">
                Serving Adani Shantigram, SG Highway and the Ahmedabad–Gandhinagar corridor.
              </p>
            </div>
            <div className="mt-6 rounded-sm border border-dashed border-forest/20 bg-sand/50 p-5">
              <p className="text-xs uppercase tracking-widest text-slate-400">Google Business Profile</p>
              <a href="#" data-testid="contact-gbp-link" className="mt-2 block text-sm font-semibold text-forest">
                Find us on Google — profile link coming soon
              </a>
            </div>
            <div className="mt-6 overflow-hidden rounded-lg">
              <iframe
                data-testid="contact-map"
                title="VR Ahmedabad Realtors — Ahmedabad location map"
                src="https://www.google.com/maps?q=Adani+Shantigram,+SG+Highway,+Ahmedabad&output=embed"
                className="h-64 w-full border-0"
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:col-span-7">
            <h2 className="font-serif text-2xl tracking-tight text-slate-900">Send us your requirement</h2>
            <p className="mt-3 text-sm text-slate-500">
              Tell us what you're looking for — an advisor will call you back.
            </p>
            <div className="mt-6">
              <LeadForm configuration="" onConfigurationChange={() => {}} project="Contact Page Enquiry" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
